

import { ComponentPortal, Portal, TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  Type,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { MwcConfigService } from 'mwc/core/config';
import { MwcSafeAny } from 'mwc/core/types';

import { MwcEmptyCustomContent, MwcEmptySize, NZ_EMPTY_COMPONENT_NAME } from './config';

function getEmptySize(componentName: string): MwcEmptySize {
  switch (componentName) {
    case 'table':
    case 'list':
      return 'normal';
    case 'select':
    case 'tree-select':
    case 'cascader':
    case 'transfer':
      return 'small';
    default:
      return '';
  }
}

type MwcEmptyContentType = 'component' | 'template' | 'string';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'mwc-embed-empty',
  exportAs: 'mwcEmbedEmpty',
  template: `
    <ng-container *ngIf="!content && specificContent !== null" [ngSwitch]="size">
      <mwc-empty *ngSwitchCase="'normal'" class="mwc-empty-normal" [mwcNotFoundImage]="'simple'"></mwc-empty>
      <mwc-empty *ngSwitchCase="'small'" class="mwc-empty-small" [mwcNotFoundImage]="'simple'"></mwc-empty>
      <mwc-empty *ngSwitchDefault></mwc-empty>
    </ng-container>
    <ng-container *ngIf="content">
      <ng-template *ngIf="contentType !== 'string'" [cdkPortalOutlet]="contentPortal"></ng-template>
      <ng-container *ngIf="contentType === 'string'">
        {{ content }}
      </ng-container>
    </ng-container>
  `
})
export class MwcEmbedEmptyComponent implements OnChanges, OnInit, OnDestroy {
  @Input() mwcComponentName?: string;
  @Input() specificContent?: MwcEmptyCustomContent;

  content?: MwcEmptyCustomContent;
  contentType: MwcEmptyContentType = 'string';
  contentPortal?: Portal<MwcSafeAny>;
  size: MwcEmptySize = '';

  private destroy$ = new Subject<void>();

  constructor(
    private configService: MwcConfigService,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private injector: Injector
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mwcComponentName) {
      this.size = getEmptySize(changes.mwcComponentName.currentValue);
    }

    if (changes.specificContent && !changes.specificContent.isFirstChange()) {
      this.content = changes.specificContent.currentValue;
      this.renderEmpty();
    }
  }

  ngOnInit(): void {
    this.subscribeDefaultEmptyContentChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private renderEmpty(): void {
    const content = this.content;

    if (typeof content === 'string') {
      this.contentType = 'string';
    } else if (content instanceof TemplateRef) {
      const context = { $implicit: this.mwcComponentName } as MwcSafeAny;
      this.contentType = 'template';
      this.contentPortal = new TemplatePortal(content, this.viewContainerRef, context);
    } else if (content instanceof Type) {
      const injector = Injector.create({
        parent: this.injector,
        providers: [{ provide: NZ_EMPTY_COMPONENT_NAME, useValue: this.mwcComponentName }]
      });
      this.contentType = 'component';
      this.contentPortal = new ComponentPortal(content, this.viewContainerRef, injector);
    } else {
      this.contentType = 'string';
      this.contentPortal = undefined;
    }

    this.cdr.detectChanges();
  }

  private subscribeDefaultEmptyContentChange(): void {
    this.configService
      .getConfigChangeEventForComponent('empty')
      .pipe(startWith(true), takeUntil(this.destroy$))
      .subscribe(() => {
        this.content = this.specificContent || this.getUserDefaultEmptyContent();
        this.renderEmpty();
      });
  }

  private getUserDefaultEmptyContent(): Type<MwcSafeAny> | TemplateRef<string> | string | undefined {
    return (this.configService.getConfigForComponent('empty') || {}).mwcDefaultEmptyContent;
  }
}
